import { Utils } from '../utils/Utils.js';

export class UIManager {
    constructor() {
        this.currentScreen = 'loading';
        this.localVideoElement = null;
        this.remoteVideoElement = null;
        this.toastContainer = null;
        this.timeouts = new Set(); // Track timeouts for cleanup
        this.isAudioOnlyMode = false; // Track audio-only mode state
        this.isMobile = false;
    }

    async init() {
        // Check if mobile
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        // Get video elements
        this.localVideoElement = document.getElementById('local-video');
        this.remoteVideoElement = document.getElementById('remote-video');
        this.toastContainer = document.getElementById('toast-container');

        // Set up mobile-specific features
        if (this.isMobile) {
            this.setupMobileFeatures();
        }

        // Don't automatically show home screen - let main.js control this
        // The loading screen will be hidden when main.js calls showScreen('home')
    }

    setupMobileFeatures() {
        // Prevent zoom on double tap
        let lastTouchEnd = 0;
        document.addEventListener('touchend', (event) => {
            const now = (new Date()).getTime();
            if (now - lastTouchEnd <= 300) {
                event.preventDefault();
            }
            lastTouchEnd = now;
        }, false);

        // Set up touch handlers for video controls
        this.setupTouchHandlers();

        // Add mobile-specific CSS
        this.addMobileStyles();
    }

    addMobileStyles() {
        const style = document.createElement('style');
        style.textContent = `
            @media (max-width: 768px) {
                .video-container {
                    height: 100vh !important;
                    width: 100vw !important;
                }
                
                .video-feed {
                    object-fit: cover !important;
                }
                
                .controls-overlay {
                    transition: transform 0.3s ease;
                }
                
                .controls-overlay.hidden {
                    transform: translateY(100%);
                }
                
                .media-controls {
                    gap: 1rem;
                }
                
                .control-btn {
                    width: 3.5rem;
                    height: 3.5rem;
                    font-size: 1.2rem;
                }
                
                .room-info {
                    font-size: 0.9rem;
                }
            }
            
            @media (max-width: 480px) {
                .container {
                    padding: 1rem;
                }
                
                .action-buttons {
                    flex-direction: column;
                    gap: 1rem;
                }
                
                .primary-btn, .secondary-btn {
                    width: 100%;
                    padding: 1rem;
                }
                
                .input-group {
                    flex-direction: column;
                    gap: 0.5rem;
                }
                
                #room-id-input {
                    width: 100%;
                    margin-bottom: 0.5rem;
                }
                
                .join-btn {
                    width: 100%;
                }
            }
        `;
        document.head.appendChild(style);
    }

    showScreen(screenName) {
        // Hide all screens
        const screens = document.querySelectorAll('.screen');
        screens.forEach(screen => {
            screen.classList.add('hidden');
        });

        // Show target screen
        const targetScreen = document.getElementById(`${screenName}-screen`);
        if (targetScreen) {
            targetScreen.classList.remove('hidden');
            this.currentScreen = screenName;

            // Set up mobile features for room screen
            if (screenName === 'room' && this.isMobile) {
                this.setupRoomMobileFeatures();
            }
        }
    }

    showError(message) {
        const errorMessage = document.getElementById('error-message');
        if (errorMessage) {
            errorMessage.textContent = message;
        }
        this.showScreen('error');
    }

    setLocalVideo(stream) {
        if (this.localVideoElement && stream) {
            this.localVideoElement.srcObject = stream;

            // Handle video track availability
            const videoTrack = stream.getVideoTracks()[0];
            if (!videoTrack) {
                this.localVideoElement.style.display = 'none';
            } else {
                // Only show video if not in audio-only mode
                this.localVideoElement.style.display = this.isAudioOnlyMode ? 'none' : 'block';
            }
        }
    }

    setAudioOnlyMode(isAudioOnly) {
        this.isAudioOnlyMode = isAudioOnly;
        if (this.localVideoElement) {
            if (isAudioOnly) {
                this.localVideoElement.style.display = 'none';
                this.showAudioOnlyIndicator();
            } else {
                this.localVideoElement.style.display = 'block';
                this.hideAudioOnlyIndicator();
            }
        }
    }

    setRemoteVideo(stream) {
        if (this.remoteVideoElement && stream) {
            this.remoteVideoElement.srcObject = stream;

            // Handle video track availability
            const videoTrack = stream.getVideoTracks()[0];
            if (!videoTrack) {
                // Show audio-only indicator
                this.showAudioOnlyIndicator();
            } else {
                this.hideAudioOnlyIndicator();
            }
        }
    }

    updateRoomInfo(roomName, roomId = null) {
        const roomIdDisplay = document.getElementById('room-id-display');

        Utils.debug('updateRoomInfo called with:', { roomName, roomId });
        Utils.debug('roomIdDisplay element:', roomIdDisplay);

        if (roomIdDisplay) {
            // Show room name prominently, room ID as subtitle if available
            if (roomId) {
                roomIdDisplay.innerHTML = `
                    <span class="room-name">${roomName}</span>
                    <span class="room-id-subtitle">Code: ${roomId}</span>
                `;
                Utils.debug('Room info updated with ID, display content:', roomIdDisplay.innerHTML);
            } else {
                roomIdDisplay.innerHTML = `<span class="room-name">${roomName}</span>`;
                Utils.debug('Room info updated without ID, display content:', roomIdDisplay.innerHTML);
            }
            
            // Verify the elements were created
            const roomNameSpan = roomIdDisplay.querySelector('.room-name');
            const roomIdSpan = roomIdDisplay.querySelector('.room-id-subtitle');
            Utils.debug('Created elements:', { 
                roomNameSpan: !!roomNameSpan, 
                roomNameText: roomNameSpan?.textContent,
                roomIdSpan: !!roomIdSpan, 
                roomIdText: roomIdSpan?.textContent 
            });
        } else {
            Utils.error('room-id-display element not found');
        }
    }

    updateConnectionStatus(status, text) {
        const statusDot = document.querySelector('.status-dot');
        const statusText = document.querySelector('.status-text');

        if (statusDot) {
            statusDot.className = 'status-dot';
            statusDot.classList.add(status);
        }

        if (statusText) {
            statusText.textContent = text;
        }
    }

    updateVideoButton(isEnabled) {
        const videoBtn = document.getElementById('toggle-video-btn');
        if (videoBtn) {
            videoBtn.className = 'control-btn';
            if (isEnabled) {
                videoBtn.classList.add('video-on');
                videoBtn.querySelector('.btn-icon').textContent = '📹';
                videoBtn.title = 'Turn off camera';
            } else {
                videoBtn.classList.add('video-off');
                videoBtn.querySelector('.btn-icon').textContent = '📹';
                videoBtn.title = 'Turn on camera';
            }
        }
    }

    updateAudioButton(isEnabled) {
        const audioBtn = document.getElementById('toggle-audio-btn');
        if (audioBtn) {
            audioBtn.className = 'control-btn';
            if (isEnabled) {
                audioBtn.classList.add('audio-on');
                audioBtn.querySelector('.btn-icon').textContent = '🎤';
                audioBtn.title = 'Turn off microphone';
            } else {
                audioBtn.classList.add('audio-off');
                audioBtn.querySelector('.btn-icon').textContent = '🎤';
                audioBtn.title = 'Turn on microphone';
            }
        }
    }

    showToast(message, type = 'info', duration = 3000) {
        const toast = document.createElement('div');
        toast.className = `toast ${type}`;
        toast.textContent = message;

        this.toastContainer.appendChild(toast);

        // Auto remove toast
        this.createTimeout(() => {
            if (toast.parentNode) {
                toast.parentNode.removeChild(toast);
            }
        }, duration);

        // Add click to dismiss
        toast.addEventListener('click', () => {
            if (toast.parentNode) {
                toast.parentNode.removeChild(toast);
            }
        });
    }

    showAudioOnlyIndicator() {
        // Create or show audio-only overlay
        let audioIndicator = document.getElementById('audio-only-indicator');
        if (!audioIndicator) {
            audioIndicator = document.createElement('div');
            audioIndicator.id = 'audio-only-indicator';
            audioIndicator.className = 'audio-only-indicator';
            audioIndicator.innerHTML = `
                <div class="audio-only-content">
                    <span class="audio-icon">🎵</span>
                    <p>Audio Only</p>
                </div>
            `;

            const videoContainer = document.querySelector('.video-container');
            videoContainer.appendChild(audioIndicator);

            // Add CSS for audio-only indicator
            const style = document.createElement('style');
            style.textContent = `
                .audio-only-indicator {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1;
                }
                
                .audio-only-content {
                    text-align: center;
                    color: white;
                }
                
                .audio-icon {
                    font-size: 4rem;
                    display: block;
                    margin-bottom: 1rem;
                    animation: pulse 2s infinite;
                }
                
                .audio-only-content p {
                    font-size: 1.5rem;
                    font-weight: 300;
                }
                
                .room-display {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 0.2rem;
                }
                
                .room-name {
                    display: block;
                    font-size: 1.2rem;
                    font-weight: 600;
                    color: #fff;
                    text-shadow: 0 1px 2px rgba(0,0,0,0.5);
                }
                
                .room-id-subtitle {
                    display: block;
                    font-size: 0.8rem;
                    color: #ccc;
                    text-shadow: 0 1px 2px rgba(0,0,0,0.5);
                }
                
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                    100% { transform: scale(1); }
                }
            `;
            document.head.appendChild(style);
        }

        audioIndicator.style.display = 'flex';
    }

    hideAudioOnlyIndicator() {
        const audioIndicator = document.getElementById('audio-only-indicator');
        if (audioIndicator) {
            audioIndicator.style.display = 'none';
        }
    }

    setupRoomMobileFeatures() {
        // Auto-hide controls after inactivity
        let inactivityTimer;
        const resetInactivityTimer = () => {
            clearTimeout(inactivityTimer);
            inactivityTimer = this.createTimeout(() => {
                this.hideControls();
            }, 3000);
        };

        // Show controls on any interaction
        const showControls = () => {
            this.showControls();
            resetInactivityTimer();
        };

        // Set up event listeners
        const videoContainer = document.querySelector('.video-container');
        if (videoContainer) {
            videoContainer.addEventListener('touchstart', showControls);
            videoContainer.addEventListener('click', showControls);
            resetInactivityTimer();
        }
    }

    showControls() {
        const controlsOverlay = document.querySelector('.controls-overlay');
        if (controlsOverlay) {
            controlsOverlay.classList.remove('hidden');
        }
    }

    hideControls() {
        const controlsOverlay = document.querySelector('.controls-overlay');
        if (controlsOverlay) {
            controlsOverlay.classList.add('hidden');
        }
    }

    // Handle orientation changes
    handleOrientationChange() {
        // Force layout recalculation
        setTimeout(() => {
            const videoContainer = document.querySelector('.video-container');
            if (videoContainer) {
                videoContainer.style.height = '100vh';
            }
        }, 100);
    }

    // Handle touch events for mobile
    setupTouchHandlers() {
        let touchStartY = 0;
        let controlsVisible = true;

        const videoContainer = document.querySelector('.video-container');
        const controlsOverlay = document.querySelector('.controls-overlay');

        if (!videoContainer || !controlsOverlay) return;

        videoContainer.addEventListener('touchstart', (e) => {
            touchStartY = e.touches[0].clientY;
        });

        videoContainer.addEventListener('touchend', (e) => {
            const touchEndY = e.changedTouches[0].clientY;
            const deltaY = touchStartY - touchEndY;

            // Swipe up to hide controls, swipe down to show
            if (Math.abs(deltaY) > 50) {
                if (deltaY > 0 && controlsVisible) {
                    // Swipe up - hide controls
                    this.hideControls();
                    controlsVisible = false;
                } else if (deltaY < 0 && !controlsVisible) {
                    // Swipe down - show controls
                    this.showControls();
                    controlsVisible = true;
                }
            } else {
                // Tap to toggle controls
                if (controlsVisible) {
                    this.hideControls();
                    controlsVisible = false;
                } else {
                    this.showControls();
                    controlsVisible = true;
                }
            }
        });
    }

    /**
     * Create a tracked timeout that can be cleaned up
     * @param {Function} callback
     * @param {number} delay
     * @returns {number} timeout ID
     */
    createTimeout(callback, delay) {
        const timeoutId = setTimeout(() => {
            this.timeouts.delete(timeoutId);
            callback();
        }, delay);
        this.timeouts.add(timeoutId);
        return timeoutId;
    }

    /**
     * Clear all tracked timeouts
     */
    clearAllTimeouts() {
        this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
        this.timeouts.clear();
    }
}

// Handle orientation change globally
window.addEventListener('orientationchange', () => {
    setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
    }, 100);
});
